import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import {
	ListItem,
	ListItemIcon,
	ListItemText,
	IconButton,
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	Typography
} from "@mui/material";

import { Cancel, Clear, CheckCircle } from "@mui/icons-material";
import { Dialog as CBDialog } from "orion-components/CBComponents";
import { attachmentService } from "client-app-core";
import { Translate, getTranslation } from "orion-components/i18n";

const propTypes = {
	attachment: PropTypes.object.isRequired,
	handleDeleteFile: PropTypes.func,
	canEdit: PropTypes.bool,
	entityType: PropTypes.string,
	dir: PropTypes.string,
	sliderImage: PropTypes.bool,
	displayAsList: PropTypes.bool,
};

const defaultProps = {
	handleDeleteFile: () => {},
	canEdit: false,
	sliderImage: false,
	displayAsList: false,
};

const FileLink = ({
	attachment,
	canEdit,
	handleDeleteFile,
	entityType,
	dir,
	sliderImage,
	displayAsList
}) => {
	const styles = {
		view: {
			width: "100%",
			cursor: "pointer",
			display: "block"
		},
		viewDiv: {
			position: "relative",
			width: "100%",
			marginBottom: "12px",
			cursor: "pointer"
		},
		sliderImage: {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			objectFit: "contain"
		},
		sliderViewDiv: {
			width: "100%",
			display: "flex",
			flexDirection: "column",
			cursor: "pointer",
		},
		deleteButton: {
			position: "absolute",
			top: 0,
			...(dir === "ltr" && { right: 0 }),
			...(dir === "rtl" && { left: 0 }),
			zIndex: 50,
			color: "rgba(255,255,255,0.7)"
		},
		defaultImageButton: {
			position: "absolute",
			top: 0,
			...(dir === "ltr" && { left: 0 }),
			...(dir === "rtl" && { right: 0 }),
			zIndex: 50,
			color: "rgba(255,255,255,0.7)",
		},
		image: {
			width: "100%",
			cursor: "pointer"
		},
		fileListItem: {
			backgroundColor: "#494D53",
			borderRadius: 5
		},
		fileName: {
			color: "#B6B6B2",
			fontFamily: "Roboto",
			fontSize: "12px",
			width: "100%",
			textAlign: "center",
			padding: "8px 4px"
		}
	};

	const [expanded, setExpanded] = useState(false);
	const [stageDelete, setStageDelete] = useState(false);
	const [isDefaultImage, setIsDefaultImage] = useState(attachment.defaultImage || false);

	const { mimeType, handle, source, filename, fileId, defaultImage } = attachment;
    
    const fileExt = mimeType ? mimeType.substring(mimeType.indexOf("/") + 1) : "";
    const isImage = mimeType?.includes("image");
    const isVideo = mimeType?.includes("video");
    
    const isExternal = source != null && source === "external";
    const mediaSrc = isExternal ? handle : `/_download?handle=${handle}`;

	useEffect(() => {
		setIsDefaultImage(attachment.defaultImage || false);
	}, [attachment.defaultImage]);

	const setDefault = () => {
		const { fileId, targetId } = attachment;
		attachmentService.updateFile(fileId, targetId, (err, response) => {
			if (!err) {
				setIsDefaultImage(true);
			}
		});
	};

	const handleStageDelete = (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setStageDelete(!stageDelete);
	};

	const handleDelete = () => {
		const { fileId } = attachment;
		handleDeleteFile(fileId);
		handleStageDelete();
	};

	const handleSetDefault = (e) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setDefault();
	};

	const videoPlayer = () => {
		return (
			<div style={styles.viewDiv}>
				{canEdit && (
					<IconButton style={styles.deleteButton} onClick={handleStageDelete}>
						<Cancel />
					</IconButton>
				)}
				<video muted autoPlay loop 
					style={styles.view} 
					onClick={() => setExpanded(true)}
				>
					<source src={mediaSrc} type={mimeType} />
				</video>
				<Typography style={styles.fileName}>
					{filename}
				</Typography>
			</div>
		);
	};

	const imageView = () => {
		return (
			<div style={sliderImage ? styles.sliderViewDiv : styles.viewDiv}>
				<div style={sliderImage ? { position: "relative", width: "100%", aspectRatio: "16/9" } : {}}>
				{canEdit && (
					<Fragment>
						<IconButton style={styles.deleteButton} onClick={handleStageDelete}>
							<Cancel />
						</IconButton>
						{(entityType === "track" || entityType === "shapes") && (
							<IconButton 
								style={styles.defaultImageButton} 
								onClick={handleSetDefault}
								disabled={isDefaultImage}
								className={isDefaultImage ? "defaultImg" : ""}
							>
								<CheckCircle />
							</IconButton>
						)}
					</Fragment>
				)}
				<img
					alt={filename}
					style={sliderImage ? styles.sliderImage : styles.image}
					src={mediaSrc}
					onClick={() => setExpanded(true)}
				/>
				</div>
				<Typography style={styles.fileName}>
					{filename}
				</Typography>
			</div>
		);
	};

	const fileView = () => {
		return (
			<ListItem
				button={true}
				component="button"
				dense={true}
				key={fileId}
				href={`${source == "external" ? "" : "/_download?handle="}${handle}`}
				download={filename}
				style={{
					...styles.fileListItem,
					paddingRight: canEdit ? 0 : 16
				}}
			>
				<ListItemIcon style={{ marginRight: 0 }}>
					<div className={`${fileExt} generic-fallback fileicon`} />
				</ListItemIcon>
				<ListItemText style={{ paddingLeft: 0 }} primary={filename} />
				{canEdit && Boolean(handleDeleteFile) && (
					<ListItemIcon>
						<IconButton style={{ padding: 0 }} onClick={(e) => handleStageDelete(e)}>
							<Cancel />
						</IconButton>
					</ListItemIcon>
				)}
			</ListItem>
		);
	};

	return (
		<Fragment>
			{attachment && (
				<div style={{ marginBottom: 12 }}>
					{isVideo ? videoPlayer() : isImage ? imageView() : fileView()}
				</div>
			)}
			<Dialog open={expanded} maxWidth="lg">
				<DialogActions>
					<IconButton onClick={() => setExpanded(false)}>
						<Clear />
					</IconButton>
				</DialogActions>
				<DialogContent style={{ overflow: "hidden" }}>
					{isVideo ? (
						<video controls style={{ maxHeight: "80vh", width: "100%" }}>
							<source src={mediaSrc} type={mimeType} />
						</video>
					) : (
						<img alt={filename} src={mediaSrc} style={{ maxHeight: "80vh", objectFit: "contain" }} />
					)}
				</DialogContent>
				<DialogActions>
					<Button fullWidth color="primary" href={mediaSrc} download={filename}>
					{isVideo ? (
						<Translate value="global.CBComponents.CBFileLink.downloadVid" />
					) : (
						<Translate value="global.CBComponents.CBFileLink.downloadImg" />
					)}
					</Button>
				</DialogActions>
			</Dialog>
			<CBDialog
				open={stageDelete}
				title={getTranslation("global.CBComponents.CBFileLink.dialog.title")}
				textContent={getTranslation("global.CBComponents.CBFileLink.dialog.textContent")}
				confirm={{
					label: getTranslation("global.CBComponents.CBFileLink.dialog.delete"),
					action: handleDelete
				}}
				abort={{
					label: getTranslation("global.CBComponents.CBFileLink.dialog.cancel"),
					action: handleStageDelete
				}}
			/>
		</Fragment>
	);
};

FileLink.propTypes = propTypes;
FileLink.defaultProps = defaultProps;

export default FileLink;
